// UC 34258 — Consolidated Demand Dashboard Page
// Mirrors RawHTMLDesign/brand-pull-order-management/consolidated-demand-dashboard-page.html
// Role: Commissary Supervisor (read-only view of aggregated demand from all pull orders)
// data-testid coverage for Playwright TC 75968.

import { useEffect, useMemo, useState, useCallback } from 'react';
import api from '../../../services/api.js';
import { MEAL_PERIOD_FILTER_OPTIONS } from '../../../utils/mealPeriods.js';

function todayStr() { return new Date().toISOString().slice(0, 10); }
function plusDays(n) {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

function priorityLabel(nearestDate) {
  if (!nearestDate) return 'Stable';
  const days = Math.ceil((new Date(nearestDate) - new Date()) / 86400000);
  if (days <= 1) return 'Critical';
  if (days <= 2) return 'Watch';
  return 'Stable';
}

function priorityStyle(p) {
  switch (p) {
    case 'Critical': return { background: '#ef4444', color: '#fff' };
    case 'Watch':    return { background: '#f97316', color: '#fff' };
    default:         return { background: '#22c55e', color: '#fff' };
  }
}

// CSV export helper
function exportToCsv(ledger) {
  const headers = ['Item Code', 'Ingredient', 'Category', 'UoM', 'Delivery Date', 'Total Qty', 'Brands', 'Open Orders'];
  const rows = ledger.map((l) => [
    l.item_code, l.item_name, l.category, l.uom,
    l.delivery_date, l.total_qty, l.brand_mix_label, l.open_order_count,
  ]);
  const csv = [headers, ...rows].map((r) => r.map((v) => `"${v ?? ''}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `consolidated-demand-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function ConsolidatedDemandDashboardPage() {
  const [brands, setBrands] = useState([]);
  const [items, setItems] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [kpi, setKpi] = useState({ open_orders: 0, ingredient_skus: 0, nearest_deadline: null });
  const [bannerDismissed, setBannerDismissed] = useState(false);

  // Filter state — default window: today → today+4
  const [filterBrand, setFilterBrand] = useState('ALL');
  const [filterCategory, setFilterCategory] = useState('ALL');
  const [filterMealPeriod, setFilterMealPeriod] = useState('ALL');
  const [filterItem, setFilterItem] = useState('ALL');
  const [filterDateStart, setFilterDateStart] = useState(todayStr());
  const [filterDateEnd, setFilterDateEnd] = useState(plusDays(4));
  const [search, setSearch] = useState('');
  const [appliedFilters, setAppliedFilters] = useState({});

  // Selection for Quick Batch Scheduler
  const [selectedKeys, setSelectedKeys] = useState(new Set());

  // Detail modal
  const [modalRow, setModalRow] = useState(null);

  // Production booking modal
  const [bookingOpen, setBookingOpen] = useState(false);
  const [bookingDate, setBookingDate] = useState(todayStr());
  const [bookingNote, setBookingNote] = useState('');
  const [bookingBusy, setBookingBusy] = useState(false);
  const [bookingResult, setBookingResult] = useState(null);

  const categories = useMemo(() => [...new Set(items.map((i) => i.category))].sort(), [items]);

  async function fetchDemand(filters = {}) {
    const params = new URLSearchParams();
    if (filters.brand_id && filters.brand_id !== 'ALL') params.set('brand_id', filters.brand_id);
    if (filters.category && filters.category !== 'ALL') params.set('category', filters.category);
    if (filters.item_id && filters.item_id !== 'ALL') params.set('item_id', filters.item_id);
    if (filters.date_start) params.set('date_start', filters.date_start);
    if (filters.date_end) params.set('date_end', filters.date_end);
    if (filters.search) params.set('search', filters.search);
    if (filters.meal_period && filters.meal_period !== 'ALL') params.set('meal_period', filters.meal_period);

    const res = await api.get(`/consolidated-demand?${params.toString()}`).catch(() => null);
    if (res) {
      setLedger(res.data.data || []);
      setKpi(res.data.kpi || { open_orders: 0, ingredient_skus: 0, nearest_deadline: null });
    }
  }

  useEffect(() => {
    Promise.all([
      api.get('/master-data/brands').then((x) => x.data.data || []).catch(() => []),
      api.get('/master-data/items').then((x) => x.data.data || []).catch(() => []),
    ]).then(([b, i]) => {
      setBrands(b);
      setItems(i);
    });
    fetchDemand({ date_start: todayStr(), date_end: plusDays(4) });
  }, []);

  function applyFilters() {
    const filters = {
      brand_id: filterBrand,
      category: filterCategory,
      item_id: filterItem,
      date_start: filterDateStart,
      date_end: filterDateEnd,
      search,
      meal_period: filterMealPeriod,
    };
    setAppliedFilters(filters);
    setSelectedKeys(new Set());
    fetchDemand(filters);
  }

  async function confirmBooking() {
    setBookingBusy(true);
    // Navigate to production scheduler with context (best-effort)
    setBookingResult(`${selectedRows.length} ingredient line(s) handed off for production on ${bookingDate}.${bookingNote ? ' Note: ' + bookingNote : ''}`);
    setBookingBusy(false);
  }

  function resetFilters() {
    setFilterBrand('ALL');
    setFilterCategory('ALL');
    setFilterMealPeriod('ALL');
    setFilterItem('ALL');
    setFilterDateStart(todayStr());
    setFilterDateEnd(plusDays(4));
    setSearch('');
    setAppliedFilters({});
    setSelectedKeys(new Set());
    fetchDemand({ date_start: todayStr(), date_end: plusDays(4) });
  }

  function toggleSelect(key) {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  const selectedRows = ledger.filter((r) => selectedKeys.has(`${r.item_id}__${r.delivery_date}`));

  const nearestDeadlineDays = kpi.nearest_deadline
    ? Math.ceil((new Date(kpi.nearest_deadline) - new Date()) / 86400000)
    : null;

  return (
    <div className="container-fluid px-4 py-4" data-testid="demand-dashboard-page">
      {/* Header */}
      <div className="row mb-4">
        <div className="col-12 d-flex justify-content-between align-items-center flex-wrap">
          <div>
            <h1 className="h3 font-weight-bold text-dark mb-1">
              <i className="fa-solid fa-layer-group mr-2 text-primary" />
              Consolidated Demand Dashboard
            </h1>
            <p className="text-muted mb-0">
              Aggregate outlet pull orders into production-ready ingredient demand, watch delivery cutoffs, and launch kitchen scheduling faster.
            </p>
          </div>
          <div className="mt-2 mt-sm-0 d-flex align-items-center flex-wrap" style={{ gap: 8 }}>
            <span className="badge badge-light border px-3 py-2 text-dark shadow-sm">
              <i className="fa-solid fa-user-shield mr-1" />Role: <strong>Commissary Supervisor</strong>
            </span>
            <button
              type="button"
              className="btn btn-warning btn-sm shadow-sm font-weight-bold"
              onClick={() => exportToCsv(ledger)}
              data-testid="btn-export-csv"
            >
              <i className="fa-solid fa-file-csv mr-1" />Export CSV
            </button>
          </div>
        </div>
      </div>

      {/* Info banner */}
      {!bannerDismissed && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="alert alert-info shadow-sm d-flex align-items-start justify-content-between" style={{ borderLeft: '5px solid #008080' }} data-testid="demand-info-banner">
              <div>
                <i className="fa-solid fa-timeline mr-2" />
                <strong>Demand Aggregation Window:</strong> Only Submitted and Acknowledged pull orders are rolled into this planning ledger.
                {' '}Use <strong>Quick Batch Scheduler</strong> to select rows and hand off to production scheduling faster.
              </div>
              <button type="button" className="close ml-3" style={{ opacity: 0.6 }} onClick={() => setBannerDismissed(true)} aria-label="Dismiss">
                <span>&times;</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* KPI Cards */}
      <div className="row mb-4">
        <div className="col-xl-3 col-md-6 mb-3">
          <div className="card h-100 shadow-sm border-0">
            <div className="card-body d-flex align-items-center">
              <div className="rounded-circle d-flex align-items-center justify-content-center mr-3 flex-shrink-0"
                style={{ width: 52, height: 52, background: '#e6f7f7' }}>
                <i className="fa-solid fa-clipboard-list" style={{ color: '#008080', fontSize: '1.3rem' }} />
              </div>
              <div>
                <small className="text-uppercase text-muted d-block" style={{ fontSize: '0.7rem', letterSpacing: '0.05em' }}>Active Open Orders</small>
                <div className="font-weight-bold" style={{ fontSize: '2rem', lineHeight: 1.1 }} data-testid="kpi-open-orders">{kpi.open_orders}</div>
                <small className="text-muted">Orders inside current filters</small>
              </div>
            </div>
          </div>
        </div>
        <div className="col-xl-3 col-md-6 mb-3">
          <div className="card h-100 shadow-sm border-0">
            <div className="card-body d-flex align-items-center">
              <div className="rounded-circle d-flex align-items-center justify-content-center mr-3 flex-shrink-0"
                style={{ width: 52, height: 52, background: '#fff7e6' }}>
                <i className="fa-solid fa-carrot" style={{ color: '#d97706', fontSize: '1.3rem' }} />
              </div>
              <div>
                <small className="text-uppercase text-muted d-block" style={{ fontSize: '0.7rem', letterSpacing: '0.05em' }}>Demanded Ingredient SKUs</small>
                <div className="font-weight-bold" style={{ fontSize: '2rem', lineHeight: 1.1 }} data-testid="kpi-ingredient-skus">{kpi.ingredient_skus}</div>
                <small className="text-muted">Distinct items to prepare</small>
              </div>
            </div>
          </div>
        </div>
        <div className="col-xl-3 col-md-6 mb-3">
          <div className="card h-100 shadow-sm border-0">
            <div className="card-body d-flex align-items-center">
              <div className="rounded-circle d-flex align-items-center justify-content-center mr-3 flex-shrink-0"
                style={{ width: 52, height: 52, background: '#fee2e2' }}>
                <i className="fa-solid fa-hourglass-half" style={{ color: '#dc2626', fontSize: '1.3rem' }} />
              </div>
              <div>
                <small className="text-uppercase text-muted d-block" style={{ fontSize: '0.7rem', letterSpacing: '0.05em' }}>Nearest Delivery Deadline</small>
                <div className="font-weight-bold" style={{ fontSize: '1.5rem', lineHeight: 1.1 }} data-testid="kpi-nearest-deadline">
                  {kpi.nearest_deadline || '--'}
                </div>
                <small className="text-muted" data-testid="kpi-deadline-caption">
                  {nearestDeadlineDays !== null ? `${nearestDeadlineDays} day(s) away` : 'No open demand'}
                </small>
              </div>
            </div>
          </div>
        </div>
        <div className="col-xl-3 col-md-6 mb-3">
          <div className="card h-100 shadow-sm border-0">
            <div className="card-body d-flex align-items-center">
              <div className="rounded-circle d-flex align-items-center justify-content-center mr-3 flex-shrink-0"
                style={{ width: 52, height: 52, background: '#d1fae5' }}>
                <i className="fa-solid fa-check-double" style={{ color: '#059669', fontSize: '1.3rem' }} />
              </div>
              <div>
                <small className="text-uppercase text-muted d-block" style={{ fontSize: '0.7rem', letterSpacing: '0.05em' }}>Selected Rows</small>
                <div className="font-weight-bold" style={{ fontSize: '2rem', lineHeight: 1.1 }} data-testid="kpi-selected-rows">{selectedKeys.size}</div>
                <small className="text-muted">Ready for production booking</small>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        {/* Filters sidebar */}
        <div className="col-xl-3 col-lg-4 mb-4">
          <div className="card shadow-sm h-100">
            <div className="card-header bg-white py-3 border-bottom">
              <h5 className="card-title mb-0 font-weight-bold text-teal">
                <i className="fa-solid fa-sliders mr-2" />Filters &amp; Planning Controls
              </h5>
            </div>
            <div className="card-body">
              <div className="form-group">
                <label className="font-weight-600 text-dark">Brand</label>
                <select
                  className="form-control custom-select"
                  value={filterBrand}
                  onChange={(e) => setFilterBrand(e.target.value)}
                  data-testid="filter-brand"
                >
                  <option value="ALL">All Brands</option>
                  {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="font-weight-600 text-dark">Ingredient Category</label>
                <select
                  className="form-control custom-select"
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                  data-testid="filter-category"
                >
                  <option value="ALL">All Categories</option>
                  {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="font-weight-600 text-dark">Meal Period</label>
                <select
                  className="form-control custom-select"
                  value={filterMealPeriod}
                  onChange={(e) => setFilterMealPeriod(e.target.value)}
                  data-testid="filter-meal-period"
                >
                  {MEAL_PERIOD_FILTER_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="font-weight-600 text-dark">Target Delivery Window</label>
                <div className="input-group input-group-sm mb-2">
                  <input type="date" className="form-control" value={filterDateStart}
                    onChange={(e) => setFilterDateStart(e.target.value)} data-testid="filter-date-start" />
                </div>
                <div className="input-group input-group-sm">
                  <input type="date" className="form-control" value={filterDateEnd}
                    onChange={(e) => setFilterDateEnd(e.target.value)} data-testid="filter-date-end" />
                </div>
              </div>
              <div className="form-group mb-4">
                <label className="font-weight-600 text-dark">Search Ingredient / Order</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Ingredient name, SKU code..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  data-testid="filter-search"
                />
              </div>
              <div className="d-flex flex-wrap">
                <button
                  type="button"
                  className="btn btn-primary btn-sm mr-2 mb-2"
                  onClick={applyFilters}
                  data-testid="btn-apply-filters"
                >
                  <i className="fa-solid fa-filter mr-1" />Apply Filters
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm mb-2"
                  onClick={resetFilters}
                  data-testid="btn-reset-filters"
                >
                  <i className="fa-solid fa-rotate-left mr-1" />Reset
                </button>
              </div>

              <hr className="my-4" />

              {/* Quick Batch Scheduler */}
              <div className="planner-panel rounded p-3 bg-light border" data-testid="quick-scheduler-panel">
                <h6 className="font-weight-bold text-dark mb-3">
                  <i className="fa-solid fa-bolt mr-1 text-primary" />Quick Batch Scheduler
                </h6>
                <div className="mb-2 small text-muted">Selected rows can be grouped into one production handoff note.</div>
                {selectedRows.length === 0 ? (
                  <p className="text-muted small" data-testid="scheduler-empty">No ingredient lines selected yet.</p>
                ) : (
                  <ul className="list-unstyled small mb-3" data-testid="scheduler-list">
                    {selectedRows.map((r) => (
                      <li key={`${r.item_id}__${r.delivery_date}`} className="mb-1">
                        <strong>{r.item_name}</strong> — {r.total_qty} {r.uom} by {r.delivery_date}
                      </li>
                    ))}
                  </ul>
                )}
                <button
                  type="button"
                  className="btn btn-primary btn-block"
                  disabled={selectedRows.length === 0}
                  onClick={() => { setBookingDate(todayStr()); setBookingNote(''); setBookingResult(null); setBookingOpen(true); }}
                  data-testid="btn-book-production"
                >
                  <i className="fa-solid fa-kitchen-set mr-1" />Book Production Run
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Ledger table */}
        <div className="col-xl-9 col-lg-8 mb-4">
          <div className="card shadow-sm h-100">
            <div className="card-header bg-white py-3 border-bottom d-flex justify-content-between align-items-center flex-wrap">
              <h5 className="card-title mb-0 font-weight-bold text-dark">
                <i className="fa-solid fa-table-list mr-2 text-primary" />Aggregated Demand Ledger
              </h5>
              <div className="d-flex align-items-center" style={{ gap: 8 }}>
                <span className="badge badge-light border px-3 py-2" data-testid="ledger-row-count">{ledger.length} Lines</span>
                <span className="text-muted small">Sorted by nearest due date first</span>
              </div>
            </div>
            <div className="card-body">
              <div className="table-responsive">
                <table className="table table-hover mb-0 text-center" data-testid="demand-ledger-table">
                  <thead>
                    <tr>
                      <th style={{ width: 50 }}>Pick</th>
                      <th className="text-left">Ingredient</th>
                      <th>Category</th>
                      <th>Brand Mix</th>
                      <th>Total Qty</th>
                      <th>UoM</th>
                      <th>Need By</th>
                      <th>Open Orders</th>
                      <th>Priority</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody data-testid="demand-ledger-tbody">
                    {ledger.length === 0 && (
                      <tr>
                        <td colSpan="10" className="text-muted py-4" data-testid="demand-ledger-empty">
                          No demand data. Submit pull orders to populate this dashboard.
                        </td>
                      </tr>
                    )}
                    {ledger.map((row) => {
                      const key = `${row.item_id}__${row.delivery_date}`;
                      const priority = priorityLabel(row.delivery_date);
                      const isSelected = selectedKeys.has(key);
                      return (
                        <tr
                          key={key}
                          className={isSelected ? 'table-primary' : ''}
                          data-testid={`demand-row-${row.item_id}-${row.delivery_date}`}
                        >
                          <td>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleSelect(key)}
                              data-testid={`demand-check-${row.item_id}-${row.delivery_date}`}
                            />
                          </td>
                          <td className="text-left">
                            <div className="font-weight-bold text-dark" data-testid={`demand-item-name-${row.item_id}-${row.delivery_date}`}>
                              {row.item_name}
                            </div>
                            <div className="text-muted small">{row.open_order_count} source order{row.open_order_count !== 1 ? 's' : ''}</div>
                          </td>
                          <td data-testid={`demand-category-${row.item_id}-${row.delivery_date}`}>{row.category}</td>
                          <td>
                            <div data-testid={`demand-brand-mix-${row.item_id}-${row.delivery_date}`} style={{ display: 'flex', flexWrap: 'wrap', gap: 3, justifyContent: 'center' }}>
                              {Object.entries(row.brand_mix || {}).map(([bn, qty]) => (
                                <span key={bn} style={{
                                  background: '#e6f7f7', color: '#006666', border: '1px solid #b2d8d8',
                                  borderRadius: 20, padding: '2px 8px', fontSize: '0.7rem', fontWeight: 600, whiteSpace: 'nowrap'
                                }}>{bn} ({qty})</span>
                              ))}
                            </div>
                          </td>
                          <td>
                            <strong data-testid={`demand-total-qty-${row.item_id}-${row.delivery_date}`}>{row.total_qty}</strong>
                          </td>
                          <td data-testid={`demand-uom-${row.item_id}-${row.delivery_date}`}>{row.uom}</td>
                          <td data-testid={`demand-date-${row.item_id}-${row.delivery_date}`}>{row.delivery_date}</td>
                          <td data-testid={`demand-orders-${row.item_id}-${row.delivery_date}`}>{row.open_order_count}</td>
                          <td>
                            <span
                              style={{ ...priorityStyle(priority), borderRadius: 20, padding: '3px 10px', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.04em', display: 'inline-block' }}
                              data-testid={`demand-priority-${row.item_id}-${row.delivery_date}`}
                            >
                              {priority.toUpperCase()}
                            </span>
                          </td>
                          <td>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => setModalRow(row)}
                              data-testid={`demand-detail-${row.item_id}-${row.delivery_date}`}
                            >
                              <i className="fa-solid fa-eye mr-1" />Details
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Production Booking Modal */}
      {bookingOpen && (
        <div className="modal fade show d-block" style={{ background: 'rgba(0,0,0,0.5)' }} data-testid="booking-modal">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content shadow-lg border-0" style={{ borderRadius: 14 }}>
              <div className="modal-header bg-teal text-white py-3">
                <h5 className="modal-title font-weight-bold">
                  <i className="fa-solid fa-kitchen-set mr-2" />Book Production Run
                </h5>
                <button type="button" className="close text-white" onClick={() => setBookingOpen(false)}>
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body p-4">
                {bookingResult ? (
                  <div className="alert alert-success" data-testid="booking-success">
                    <i className="fa-solid fa-circle-check mr-2" />{bookingResult}
                  </div>
                ) : (
                  <>
                    <p className="text-muted small mb-3">
                      The following <strong>{selectedRows.length}</strong> ingredient line(s) will be handed off to the production scheduler:
                    </p>
                    <ul className="list-group list-group-flush mb-4" data-testid="booking-lines">
                      {selectedRows.map((r) => (
                        <li key={`${r.item_id}__${r.delivery_date}`} className="list-group-item py-2 px-0">
                          <strong>{r.item_name}</strong>
                          <span className="text-muted ml-2">{r.item_code}</span>
                          <span className="float-right font-weight-bold text-teal">{r.total_qty} {r.uom}</span>
                          <div className="small text-muted">Need by {r.delivery_date} · {r.open_order_count} order(s)</div>
                        </li>
                      ))}
                    </ul>
                    <div className="form-group">
                      <label className="font-weight-600">Target Production Date <span className="text-danger">*</span></label>
                      <input type="date" className="form-control" value={bookingDate}
                        onChange={(e) => setBookingDate(e.target.value)} data-testid="booking-date" />
                    </div>
                    <div className="form-group mb-0">
                      <label className="font-weight-600">Supervisor Note (optional)</label>
                      <textarea className="form-control" rows={2}
                        placeholder="Any special instructions for the commissary team..."
                        value={bookingNote} onChange={(e) => setBookingNote(e.target.value)}
                        data-testid="booking-note"
                      />
                    </div>
                  </>
                )}
              </div>
              <div className="modal-footer bg-light">
                <button type="button" className="btn btn-secondary" onClick={() => setBookingOpen(false)}>
                  {bookingResult ? 'Close' : 'Cancel'}
                </button>
                {!bookingResult && (
                  <button type="button" className="btn btn-primary px-4" onClick={confirmBooking}
                    disabled={bookingBusy || !bookingDate} data-testid="btn-confirm-booking">
                    <i className="fa-solid fa-check mr-1" />{bookingBusy ? 'Booking...' : 'Confirm Handoff'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {modalRow && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          data-testid="demand-detail-modal"
        >
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content shadow-lg border-0" style={{ borderRadius: 14 }}>
              <div className="modal-header bg-teal text-white py-3">
                <h5 className="modal-title font-weight-bold">
                  <i className="fa-solid fa-boxes-stacked mr-2" />Demand Line Breakdown
                </h5>
                <button type="button" className="close text-white" onClick={() => setModalRow(null)} data-testid="modal-close">
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body p-4">
                <div className="row mb-4">
                  <div className="col-md-6 border-right">
                    <h6 className="font-weight-bold text-teal mb-3">Ingredient Summary</h6>
                    <table className="table table-sm table-borderless mb-0">
                      <tbody>
                        <tr>
                          <th className="text-muted" style={{ width: '40%', fontWeight: 'normal' }}>Ingredient:</th>
                          <td data-testid="modal-ingredient-name">{modalRow.item_name}</td>
                        </tr>
                        <tr>
                          <th className="text-muted" style={{ fontWeight: 'normal' }}>Category:</th>
                          <td data-testid="modal-ingredient-category">{modalRow.category}</td>
                        </tr>
                        <tr>
                          <th className="text-muted" style={{ fontWeight: 'normal' }}>Total Qty:</th>
                          <td data-testid="modal-ingredient-qty">{modalRow.total_qty} {modalRow.uom}</td>
                        </tr>
                        <tr>
                          <th className="text-muted" style={{ fontWeight: 'normal' }}>Need By:</th>
                          <td data-testid="modal-ingredient-deadline">{modalRow.delivery_date}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="col-md-6 pl-md-4">
                    <h6 className="font-weight-bold text-teal mb-3">Brand Demand Split</h6>
                    <div data-testid="modal-brand-split" className="small text-dark">
                      {Object.entries(modalRow.brand_mix).map(([bn, qty]) => (
                        <div key={bn} className="mb-1">
                          <strong>{bn}:</strong> {qty} {modalRow.uom}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <hr className="my-3" />
                <h6 className="font-weight-bold text-dark mb-2">Source Order Contributions</h6>
                <div className="table-responsive border rounded bg-white">
                  <table className="table table-sm table-hover mb-0 text-center">
                    <thead>
                      <tr>
                        <th>Order ID</th>
                        <th className="text-left">Brand</th>
                        <th className="text-left">Outlet</th>
                        <th>Delivery Date</th>
                        <th>Qty</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody data-testid="modal-source-orders">
                      {modalRow.source_orders.map((s, idx) => (
                        <tr key={idx}>
                          <td><small>{s.order_id.slice(0, 8)}…</small></td>
                          <td className="text-left">{s.brand_name}</td>
                          <td className="text-left">{s.outlet_name}</td>
                          <td>{s.delivery_date}</td>
                          <td>{s.quantity}</td>
                          <td><span className="badge badge-info">{s.status}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="modal-footer bg-light">
                <button type="button" className="btn btn-secondary px-4" onClick={() => setModalRow(null)} data-testid="modal-close-footer">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
